/**
 * Height of the on-screen keyboard, in CSS pixels (design spec §2 / §7).
 *
 * Mobile browsers do not resize the layout viewport when the keyboard opens —
 * they shrink the *visual* viewport and scroll the page. Without compensating,
 * a bottom sheet's input and its action button end up underneath the keyboard.
 * `visualViewport` is the only reliable signal for how much was taken.
 *
 * Returns 0 where the API is unavailable, so callers can add it unconditionally.
 */

import { useEffect, useState } from "react";

export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      // The slice of the layout viewport the keyboard (and any browser chrome
      // pinned to the bottom) is covering.
      const covered = window.innerHeight - vv.height - vv.offsetTop;
      // Small deltas are toolbar jitter, not a keyboard.
      setInset(covered > 80 ? Math.round(covered) : 0);
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  return inset;
}
